// @ts-nocheck
// ─────────────────────────────────────────────────────────────────────
// helpDrawer · Contextual help, scoped to the current view.
//
// Each view has a help entry describing:
//   • What this view is for (1-2 lines)
//   • Common tasks with concrete examples
//   • Key concepts (with definitions)
//   • Keyboard shortcuts
//
// Used via:
//   import { openHelpDrawer } from '../components/helpDrawer';
//   openHelpDrawer('staged');  // or whatever the view id is
// ─────────────────────────────────────────────────────────────────────

import { store } from '../state/store';

// ─────────────────────────────────────────────────────────────────────
// Help content for every view
// ─────────────────────────────────────────────────────────────────────

export const HELP = {
  // ─── Conversation views ──────────────────────────────────────────
  conversation: {
    title: 'Conversations with Blyn',
    intro: 'Talk to Blyn the way you\'d brief a sharp colleague. Blyn understands direct-mail context: campaigns, audiences, journeys, drops, KB sources, your historical results.',
    tasks: [
      {
        title: 'Ask a planning question',
        example: '"I have a $50K budget for Q3 winback. Walk me through 2 vs 6 drops with realistic forecasts."',
        outcome: 'Blyn pulls comparable campaigns and gives you a forecast with confidence intervals.',
      },
      {
        title: 'Stage a campaign instead of executing it',
        example: 'Turn on Stage mode (the chip above the composer), then ask: "Launch Drop 3 next Tuesday with variants A and B."',
        outcome: 'Blyn won\'t execute. The action shows up in Staged Actions for your approval.',
      },
      {
        title: 'Show your work to the team',
        example: 'Switch to Expert mode to see Blyn\'s plan, tool calls, and reasoning. Use the share icon to send a link.',
        outcome: 'Anyone in your tenant can open the conversation and see the full Trace.',
      },
      {
        title: 'Cite specific KB sources',
        example: 'Click the KB chip above the composer. Toggle which sources Blyn should reference in this chat.',
        outcome: 'Blyn will cite only the sources you turned on. KB chip shows the active count.',
      },
    ],
    concepts: [
      { term: 'Simple vs Expert', def: 'Simple shows only Blyn\'s answer. Expert reveals the agent loop. Planning, tool calls, memory retrieval, reasoning. Toggle in the top right.' },
      { term: 'Artifact / Memory / Trace', def: 'Side-panel tabs. Artifact = the chart or doc Blyn made. Memory = scope-aware view of what Blyn knows about you. Trace = full agent steps (Expert mode only).' },
      { term: 'Memory scopes', def: 'Project facts apply only within one project. Tenant facts apply across all your projects. Global facts are platform-wide patterns shown with attribution. Each fact is scoped at extraction time and can be promoted later.' },
      { term: 'Re-verification', def: 'When a fact\'s underlying evidence is more than 90 days old, Blyn surfaces it for you to confirm. Three actions: Still true (resets the clock), Remove (archives the fact), Edit (rewrite with manual provenance).' },
      { term: 'Stage mode', def: 'When ON, Blyn proposes actions for your approval instead of executing them. A yellow banner appears above the composer.' },
    ],
    shortcuts: [['Enter', 'Send message'], ['Shift+Enter', 'New line'], ['Cmd+K', 'Open command palette'], ['Cmd+N', 'New conversation']],
  },

  'new-conversation': {
    title: 'Starting a new conversation',
    intro: 'Pick a project to scope the chat, then either type a question or click a starter to seed the conversation.',
    tasks: [
      {
        title: 'Scope a chat to a project',
        example: 'Click the project picker (top-left) and select "Q3 Winback Campaign". Now Blyn cites that project\'s KB and audiences first.',
        outcome: 'Blyn\'s suggestions are anchored in your project\'s data, not the whole tenant.',
      },
      {
        title: 'Use a starter prompt',
        example: 'Click any starter ("Check on a running campaign", "Diagnose underperformance") to seed the conversation with a concrete question.',
        outcome: 'Saves you typing. You can edit before sending.',
      },
    ],
    concepts: [
      { term: 'Project scope', def: 'Conversations attached to a project get that project\'s KB sources, audiences, and history in context. Unattached chats see only tenant-wide context.' },
    ],
    shortcuts: [['⌘N', 'New conversation'], ['⌘K', 'Command palette']],
  },

  // ─── Projects ─────────────────────────────────────────────────────
  projects: {
    title: 'Projects',
    intro: 'A project bundles together everything for one campaign or initiative: conversations, audiences, journeys, assets, staged actions, KB sources.',
    tasks: [
      {
        title: 'Create a new project',
        example: 'Click + New Project. Name it, pick a vertical (financial services, retail, etc), invite collaborators.',
        outcome: 'You get a workspace with 7 tabs: Overview, Conversations, Assets, Audiences, Journeys, Staged actions, Activity.',
      },
      {
        title: 'Filter by status',
        example: 'Click the filter pills: Live (currently shipping), Draft (still being built), Archived (paused or done).',
        outcome: 'Cleaner list, faster navigation.',
      },
      {
        title: 'Pin frequent projects',
        example: 'Pinned projects appear in the left sidebar for one-click access.',
        outcome: 'Quick context switching without searching.',
      },
    ],
    concepts: [
      { term: 'Project vs tenant scope', def: 'Projects have private KB sources, audiences, and conversations. Tenant scope means available across all projects.' },
    ],
    shortcuts: [['⌘P', 'Jump to project']],
  },

  'project-workspace': {
    title: 'Project workspace',
    intro: 'Everything for one campaign in one place. Use the tabs to move between Overview, Conversations, Audiences, Journeys, Assets, Staged actions, and Activity.',
    tasks: [
      {
        title: 'Check campaign health at a glance',
        example: 'Open the Overview tab. The 4 stat tiles at top show audience size, drops shipped, spend vs budget, lift so far.',
        outcome: 'You can tell in 5 seconds whether the campaign is on track.',
      },
      {
        title: 'Edit an audience',
        example: 'Audiences tab → click Edit on the audience you want to change. The criteria builder opens with that audience loaded.',
        outcome: 'You modify criteria, see the live record-count preview update, save when ready.',
      },
      {
        title: 'Refresh an audience from source',
        example: 'Audiences tab → click Refresh on a LIVE audience. Blyn re-queries HubSpot or Snowflake.',
        outcome: 'Fresh records pulled in. Locked audiences (control cells) can\'t be refreshed.',
      },
      {
        title: 'Add a KB source to the project',
        example: 'Overview tab → click "Manage sources →" next to the KB section.',
        outcome: 'Opens the KB view. Upload a file to make it available to Blyn in this project\'s conversations.',
      },
    ],
    concepts: [
      { term: 'Live / Draft / Staged / Locked', def: 'Status pills on audiences. Live = actively used. Draft = being built. Staged = waiting your approval. Locked = control cell, frozen for statistical integrity.' },
      { term: 'Tenant vs project KB', def: 'Tenant KB sources are available everywhere. Project KB sources only appear in this project\'s conversations.' },
    ],
    shortcuts: [['⌘K', 'Command palette'], ['1-7', 'Switch tabs']],
  },

  // ─── Staged actions. THIS ONE IS DETAILED ON PURPOSE ────────────
  staged: {
    title: 'Staged actions',
    intro: 'Staged actions are things Blyn wants to do that need your approval first. Nothing happens until you click Approve. This is how you keep an AI agent safe on real spend and real customer data.',
    tasks: [
      {
        title: 'How an action gets staged',
        example: 'Blyn proposes an action three ways: (1) you say "stage this", (2) the action is high-stakes by default (real spend, destructive), or (3) you turned on Stage mode in the chat.',
        outcome: 'The action shows up here with a clear summary and any warnings.',
      },
      {
        title: 'Approve & launch',
        example: 'Click the green primary button. You see a confirmation modal with action type, project, priority, and any warnings. Confirm to execute.',
        outcome: 'Action transitions PROPOSED → APPROVED → EXECUTED. It moves to "Recently approved" at the bottom of the page.',
      },
      {
        title: 'Edit fields before approving',
        example: 'Click "Edit fields" on a card. A popover opens with the editable fields for that action type. Ship date, record cap, spend cap, notes.',
        outcome: 'After saving, the card shows a prominent EDITED banner. Re-review before approving.',
      },
      {
        title: 'Reject a proposed action',
        example: 'Click Cancel or Reject. You\'ll see a confirmation. Blyn gets the rejection in its audit log and won\'t auto-propose the same thing again.',
        outcome: 'Action is removed from the queue.',
      },
      {
        title: 'Approve in bulk',
        example: 'Click "Approve all N non-destructive" in the header. A confirmation lists everything that\'ll be approved. Destructive actions are excluded.',
        outcome: 'Each action transitions independently. You\'ll see them all execute.',
      },
    ],
    concepts: [
      { term: 'PROPOSED', def: 'Blyn has staged this. It\'s waiting for you. Nothing has happened.' },
      { term: 'APPROVED', def: 'You clicked approve. The action is now executing.' },
      { term: 'EXECUTED', def: 'The action ran successfully. Visible in "Recently approved".' },
      { term: 'EDITED', def: 'You changed one or more fields before approving. The card shows a banner reminding you to re-review.' },
      { term: 'DESTRUCTIVE', def: 'The action removes, archives, or otherwise damages something. Excluded from bulk approve.' },
      { term: 'High priority', def: 'Time-sensitive or higher-stakes. Usually involves real spend or external systems.' },
    ],
    shortcuts: [],
  },

  // ─── Knowledge base ──────────────────────────────────────────────
  knowledge: {
    title: 'Knowledge base',
    intro: 'Documents Blyn cites when reasoning. Tenant-scope sources are available everywhere; project-scope sources are only in that project.',
    tasks: [
      {
        title: 'Upload a source',
        example: 'Click "Upload source" → pick a PDF, DOCX, CSV, or MD. Optionally check "Scope to current project only".',
        outcome: 'Blyn indexes it (you\'ll see "indexing…" briefly), then it\'s available to cite. The chunk count updates.',
      },
      {
        title: 'Filter to find a source',
        example: 'Use the pills (All / Tenant / Project / PDFs / Docs / Data) or type in the search box.',
        outcome: 'Live filter. Counts on each pill update.',
      },
      {
        title: 'Inspect or remove a source',
        example: 'Click any source card. The detail popover shows chunks indexed, citations made, last cited timestamp, and actions: Re-index / Download / Remove.',
        outcome: 'Removing a source removes future citations. Historical citations remain in the audit log.',
      },
    ],
    concepts: [
      { term: 'Chunks', def: 'Indexed pieces of the source. Larger chunks = more context per citation, smaller = more precise matching.' },
      { term: 'Tenant vs Project scope', def: 'Tenant sources are visible everywhere. Project sources are isolated to one project.' },
    ],
    shortcuts: [['⌘F', 'Focus search']],
  },

  // ─── Forecast / Insights ─────────────────────────────────────────
  forecast: {
    title: 'Insights & forecasts',
    intro: '37 insights across 9 categories. Drops, persona, propensity, channel, creative, geographic, timing, offer, attribution. Each is grounded in your campaign history plus cross-tenant comparables.',
    tasks: [
      {
        title: 'Browse by category',
        example: 'Use the category pills at top. The numbered sub-tabs below show each insight in that category (e.g. "1. 6 drops produces 38% more revenue than 2").',
        outcome: 'Click any sub-tab to jump to that insight.',
      },
      {
        title: 'Act on an insight',
        example: 'Each insight has a primary CTA at the bottom (e.g. "Draft a 6-drop journey", "Declare Variant B winner"). Click it.',
        outcome: 'Routes you to the relevant flow (journey editor, audience builder, etc) or stages an action.',
      },
      {
        title: 'Check the methodology',
        example: 'Click "Why this confidence?". You\'ll see the basis (e.g. "847 comparable campaigns · cross-tenant") and the method tag.',
        outcome: 'Understand how confident you should be in this insight.',
      },
      {
        title: 'Export an insight',
        example: 'Click Export → PDF, CSV, share link, or embed in conversation.',
        outcome: 'Useful for sharing with stakeholders who aren\'t in the app.',
      },
    ],
    concepts: [
      { term: 'Method tag', def: '"High-confidence internal" = derived from your own campaigns. "Mixed signal" = blend of your campaigns and cross-tenant data. "Cross-tenant" = no internal data yet.' },
      { term: 'Confidence %', def: 'How sure Blyn is the insight will hold for your next campaign. Above 85% = high confidence.' },
      { term: 'Comparable campaigns', def: 'Matched on audience size, vertical, dormancy depth, offer structure. Your own campaigns weighted 2x.' },
    ],
    shortcuts: [['1-9', 'Switch category'], ['← →', 'Previous/next insight']],
  },

  // ─── Nudges ──────────────────────────────────────────────────────
  nudges: {
    title: 'Nudges',
    intro: 'Blyn watches your platform state and surfaces things you might want to know about. Underperforming drops, test results, untouched audiences, stale artifacts.',
    tasks: [
      {
        title: 'Investigate a nudge',
        example: 'Click "Investigate" on a performance alert. Blyn opens a conversation pre-seeded with the diagnostic question.',
        outcome: 'You skip the typing and go straight into root-cause analysis.',
      },
      {
        title: 'Act on a recommendation',
        example: 'Click "Declare winner" or "Upload to platform" on a nudge. A confirmation modal shows what will be staged.',
        outcome: 'A new staged action appears. The nudge auto-dismisses since it\'s been actioned.',
      },
      {
        title: 'Dismiss a nudge',
        example: 'Click Dismiss. It\'s hidden but restorable. Or "Dismiss · don\'t suggest again" to permanently silence that nudge type.',
        outcome: 'Dismissed nudges appear in a "Dismissed this session" footer with Restore links.',
      },
      {
        title: 'Tune which nudges fire',
        example: 'Click Settings (top right). Toggle category-level on/off plus quiet hours.',
        outcome: 'Cuts down noise without losing critical alerts.',
      },
    ],
    concepts: [
      { term: 'Performance alert', def: 'A campaign is underperforming its forecast. Severity = red.' },
      { term: 'Threshold crossed', def: 'A test reached statistical significance or a spend cap was hit. Severity = green or yellow.' },
      { term: 'Opportunity', def: 'A segment or audience that\'s ready for action but hasn\'t been touched. Severity = yellow.' },
      { term: 'Stale artifact', def: 'Drafts, uploads, or test results that have been sitting around. Severity = info.' },
      { term: 'Pattern interrupted', def: 'You usually do X after Y, but this time you didn\'t. Severity = purple.' },
    ],
    shortcuts: [],
  },

  // ─── Approvals queue ──────────────────────────────────────────────
  approvals: {
    title: 'Approvals queue',
    intro: 'Cross-project items waiting for a human decision. Distinct from Staged Actions: those come from Blyn, these come from people (or compliance auto-flags).',
    tasks: [
      {
        title: 'Review and approve',
        example: 'Click any pending item. You see the requester, the description, severity, and a decision panel.',
        outcome: 'You can approve, reject, or escalate. Decision goes into the audit log.',
      },
      {
        title: 'Set approval rules',
        example: 'Click Approval rules in the header to set auto-approval thresholds, escalation rules, and approver chains.',
        outcome: 'Less manual review on routine items.',
      },
    ],
    concepts: [
      { term: 'Staged vs Approvals', def: 'Staged = Blyn proposed it. Approvals = a person or auto-flag proposed it. Both end in execute or reject.' },
    ],
    shortcuts: [],
  },

  notifications: {
    title: 'Notifications',
    intro: 'A log of everything Blyn or your collaborators did. Click any item to jump to the relevant place.',
    tasks: [
      {
        title: 'Filter by type',
        example: 'Use the filter pills: all / approvals / actions / mentions / system.',
        outcome: 'Focus on what you need to see.',
      },
      {
        title: 'Mark all read',
        example: 'Click "Mark all read" in the header.',
        outcome: 'Notification count badge clears.',
      },
    ],
    concepts: [],
    shortcuts: [],
  },

  compose: {
    title: 'Creative composer',
    intro: 'Generate, review, and stage direct-mail creatives. Variants are AI-generated using your brand voice and project context.',
    tasks: [
      {
        title: 'Generate variants',
        example: 'Click "Generate variants". Blyn produces 3 postcard variants in your brand voice.',
        outcome: 'You see them side by side with predicted lift for each.',
      },
      {
        title: 'Stage approved variants',
        example: 'Pick the variants you want, click "Stage for review". They go to Staged Actions for approval.',
        outcome: 'After approval, the variants are uploaded to the platform and available in campaigns.',
      },
    ],
    concepts: [
      { term: 'Variant', def: 'A creative permutation. Variants differ on headline, image, CTA, or layout. Testable individually.' },
    ],
    shortcuts: [],
  },

  settings: {
    title: 'Settings',
    intro: 'Configure Blyn\'s memory, integrations, billing, members, and tenant-wide policies.',
    tasks: [
      {
        title: 'Edit memory',
        example: 'Memory tab → see facts grouped by scope (project / tenant / global). Click the pencil to edit, the folder to change scope, the pin to keep it from decaying.',
        outcome: 'Manual edits become trusted (confidence 1.0, pinned). Blyn applies the change immediately to all future conversations.',
      },
      {
        title: 'Re-verify a stale fact',
        example: 'When the yellow banner appears at the top of the Memory tab, choose Still true (confirms), Remove (archives), or Edit (rewrites).',
        outcome: 'Re-verified facts get their evidence count bumped. Removed facts move to ARCHIVED but stay in audit history.',
      },
      {
        title: 'Adjust the memory cost cap',
        example: 'When the budget bar reaches 70%, click Raise cap. Default is $50/month. Increases up to $200/month are available without contacting support.',
        outcome: 'Higher cap means Blyn can keep learning without pausing. If you hit the cap, existing memory still works but new learning stops until the next month or you raise the cap.',
      },
      {
        title: 'Connect an integration',
        example: 'Integrations tab → click Connect on HubSpot, Snowflake, etc.',
        outcome: 'After OAuth, Blyn can query that source for audiences and metrics.',
      },
    ],
    concepts: [
      { term: 'Scope · project / tenant / global', def: 'Project facts apply only when that project is the active scope. Tenant facts apply everywhere in your account. Global facts are platform-wide observations shown read-only with attribution.' },
      { term: 'Origin · stated / observed / inferred / manual', def: 'How Blyn learned each fact. STATED = you said it explicitly. OBSERVED = Blyn inferred it from your behavior. INFERRED = derived from billing or integrations. MANUAL = you added or edited it directly (highest trust).' },
      { term: 'Patterns', def: 'Higher-level claims synthesized from 3+ related facts. Compaction promotes patterns when underlying facts converge. Patterns show with their category (PREFERENCE / WORKFLOW / CONSTRAINT) and underlying-fact count.' },
      { term: 'Budget cap', def: 'A monthly LLM-cost ceiling per tenant. When reached, memory learning pauses; retrieval keeps working. Default $50/mo. Raise it from the Memory tab when needed.' },
    ],
    shortcuts: [],
  },

  'audit-log': {
    title: 'Audit log',
    intro: 'Every Blyn action, every human approval, every config change. Designed for compliance review and post-hoc debugging.',
    tasks: [
      {
        title: 'Filter the log',
        example: 'Click Filter. Filter by user, project, action type, date range.',
        outcome: 'Focused log entries.',
      },
      {
        title: 'Export for compliance',
        example: 'Click Export. Choose CSV or JSON.',
        outcome: 'Export queued. Sent to your email when ready.',
      },
    ],
    concepts: [],
    shortcuts: [],
  },

  search: {
    title: 'Search',
    intro: 'Search across conversations, projects, audiences, journeys, KB sources, and people.',
    tasks: [
      {
        title: 'Quick search',
        example: 'Type any keyword. Results are ranked by recency and relevance.',
        outcome: 'Click any result to jump to it.',
      },
    ],
    concepts: [],
    shortcuts: [['⌘K', 'Open search anywhere']],
  },

  help: {
    title: 'Help center',
    intro: 'Documentation, tutorials, and support channels.',
    tasks: [],
    concepts: [],
    shortcuts: [],
  },

  'first-run': {
    title: 'Getting started',
    intro: 'Welcome to Blyn. This first-run flow gets you to a useful state in three steps: connect a data source, upload a brand voice guide, run a sample campaign.',
    tasks: [],
    concepts: [],
    shortcuts: [],
  },

  tasks: {
    title: 'Running tasks',
    intro: 'A staged action is a single decision Blyn wants to make. A running task is something Blyn does on your behalf over hours, days, or weeks. Monitoring a campaign, running an A/B test, refreshing an audience on a schedule. This is where you watch Blyn work.',
    tasks: [
      {
        title: 'Start a long-running task',
        example: 'In a conversation, say "Watch Q3 Winback through all 6 drops and alert me if any drop underperforms forecast by 15%+".',
        outcome: 'Blyn stages the task for your approval, then begins monitoring. Each checkpoint (each drop) reports back automatically.',
      },
      {
        title: 'Respond when Blyn needs you',
        example: 'When a task hits a "NEEDS YOU" state, it pauses execution and shows you the options. The Drop 2 underperformance case shows this. Blyn proposes 4 alternatives, you pick one.',
        outcome: 'Your decision is logged on the checkpoint and Blyn resumes with that choice applied.',
      },
      {
        title: 'Pause or cancel a task',
        example: 'Use the Pause button to hold a task without losing progress. Cancel kills it permanently. Already-executed work (shipped mail, real spend) is not undone.',
        outcome: 'Paused tasks resume from the same checkpoint when you click Resume.',
      },
      {
        title: 'Recover from a failure',
        example: 'When a task fails (e.g. Snowflake query timeout), the checkpoint that broke is shown clearly with recovery options. Click Retry to re-run from that checkpoint.',
        outcome: 'Retry resets the failed checkpoint to pending. Useful for transient errors.',
      },
      {
        title: 'See Blyn\'s track record',
        example: 'Click "Track record →" to see how often Blyn\'s forecasts have hit on your campaigns over time.',
        outcome: 'Calibration data. Does a 90% confidence forecast actually hit 90% of the time?. Helps you decide how much weight to give a recommendation.',
      },
    ],
    concepts: [
      { term: 'Checkpoint', def: 'A discrete event in a task\'s life. Drop 1 shipped, AUC computed, human decision needed. Each has a status (passed/failed/waiting/pending) and a timestamp.' },
      { term: 'Human decision checkpoint', def: 'When Blyn can\'t proceed without your input. Task pauses, shows options with impact predictions, resumes after you decide.' },
      { term: 'Outcome', def: 'For completed tasks, the final result vs forecast. Forecast hit vs miss, with delta and explanation. Feeds into Blyn\'s track record.' },
      { term: 'NEEDS YOU vs RUNNING', def: 'RUNNING means Blyn is working and will tell you when it has something. NEEDS YOU means Blyn is blocked until you respond. This is urgent.' },
    ],
    shortcuts: [['ESC', 'Deselect task']],
  },

  'track-record': {
    title: 'Blyn\'s track record',
    intro: 'Every forecast Blyn makes. Campaign lift, audience predictions, A/B test outcomes, channel mix. Gets logged and compared against reality when the campaign closes. This view shows the aggregate: is Blyn calibrated, what\'s it best at, what\'s it worst at?',
    tasks: [
      {
        title: 'Check overall calibration',
        example: 'The top stats show hit rate and bias. Hit rate = % of forecasts within their stated confidence interval. Bias = does Blyn systematically over- or under-forecast?',
        outcome: 'A hit rate of 75%+ with bias under ±5% means Blyn is well-calibrated. Outside that, weight recommendations accordingly.',
      },
      {
        title: 'Look at confidence calibration',
        example: 'The middle chart compares expected hit rate (e.g. 85% for a 80-89% confidence bucket) vs actual hit rate.',
        outcome: 'If 90% confidence forecasts hit 60% of the time, Blyn is over-confident in that range. Discount its 90%+ claims.',
      },
      {
        title: 'See category-level strengths',
        example: 'The "By category" section shows hit rate per type. Campaign lift, audience, timing, creative, etc.',
        outcome: 'You\'ll see where Blyn is reliable (e.g. creative variants) vs where it struggles (e.g. audience predictions in new verticals).',
      },
      {
        title: 'Investigate a specific miss',
        example: 'Each forecast card shows the original prediction, actual outcome, delta percentage, and a one-line explanation of what drove the difference.',
        outcome: 'Useful for understanding edge cases and refining your prompts.',
      },
    ],
    concepts: [
      { term: 'Hit vs miss', def: 'A forecast hits when actual falls within the stated confidence interval. Outside the CI = miss, regardless of direction.' },
      { term: 'Calibration', def: 'Average absolute distance between forecast and actual, as a percentage. Lower = better.' },
      { term: 'Bias', def: 'Signed average delta. Positive = Blyn under-forecasts on average. Negative = over-forecasts.' },
    ],
    shortcuts: [],
  },

  creatives: {
    title: 'Creatives',
    intro: 'Every creative template across every channel. Pick one on the left to see its variants, lift metrics, and approve winners.',
    tasks: [
      {
        title: 'Browse by channel',
        example: 'Use the filter pills at the top of the list to narrow down to email, SMS, or direct mail. The count on each pill shows how many creatives are in that channel.',
        outcome: 'List on the left updates immediately.',
      },
      {
        title: 'Review variants',
        example: 'Each creative has a base template body plus N variants. Each variant shows a sample render with placeholder values filled in. Lift and significance update as ARC collects more responses.',
        outcome: 'You can see which variant is winning before deciding to roll it out.',
      },
      {
        title: 'Approve a winner',
        example: 'Click "Approve as winner" on a variant. The button is enabled once the variant reaches 90% significance with positive lift. Approval emits a CREATIVE_APPROVED event to ARC.',
        outcome: 'ARC rolls the winning variant out to the remaining audience. Other variants are marked LOST in the UI.',
      },
      {
        title: 'Edit the base template',
        example: 'Click "Edit template" in the detail header. Opens the template editor with the current body and variable picker.',
        outcome: 'Changes save back into this creative, emitting TEMPLATE_UPDATED to ARC.',
      },
    ],
    concepts: [
      { term: 'Base template', def: 'The starting point for variants. Variants inherit unspecified fields from the base.' },
      { term: 'Variant', def: 'An A/B/C variation of the template. Each tracks its own impressions, lift, and significance.' },
      { term: 'Significance', def: 'Statistical confidence that the lift is real rather than chance. We require ≥90% before allowing approval.' },
    ],
    shortcuts: [],
  },

  'web-personalization': {
    title: 'Web personalization',
    intro: 'Zones on your site that ARC personalizes. Each zone has an autonomy level controlling how much ARC can act on its own.',
    tasks: [
      {
        title: 'Adjust autonomy',
        example: 'Click the autonomy badge on any zone to cycle through L0 (paused) → L1 (suggest only) → L2 (act + report) → L3 (full auto). The change emits ZONE_CONFIG_UPDATED to ARC.',
        outcome: 'Higher autonomy means ARC needs fewer approvals. The pulsing dot signals the level is active.',
      },
      {
        title: 'Edit zone config',
        example: 'Click "Edit config" on a zone. A form modal opens with every config field (rotation strategy, slot count, fallbacks, locked flags). Save to push the change.',
        outcome: 'JSON values are validated before save. ARC receives the new config and applies it on the next render.',
      },
      {
        title: 'Read zone metrics',
        example: 'Each zone shows impressions, clicks, CTR, conversions, and lift over the last 30 days. Metrics update reactively as ZONE_RENDERED/IMPRESSION/CLICK/CONVERT events arrive from ARC.',
        outcome: 'You can spot underperforming zones at a glance.',
      },
    ],
    concepts: [
      { term: 'Autonomy', def: 'How much ARC can act without approval. L0 paused, L1 suggests, L2 acts within boundaries, L3 fully autonomous.' },
      { term: 'Zone', def: 'A personalized slot on a page (hero, product rec, urgency bar, etc). Each can be configured and gated independently.' },
      { term: 'Lift', def: 'Conversion rate improvement vs the unpersonalized control. Positive = good.' },
    ],
    shortcuts: [],
  },

  'product-catalog': {
    title: 'Product catalog',
    intro: 'Source of truth for product data. Saved products sync to ARC and become available in personalization templates via the product.* variable namespace.',
    tasks: [
      {
        title: 'Search and filter',
        example: 'Use the search box for name, SKU, or category. Filter pills narrow by category (Appliances, Services, Parts) or status (active, low stock, archived).',
        outcome: 'Table updates immediately. Counts on pills show how many products match.',
      },
      {
        title: 'Create a new product',
        example: 'Click "New product" in the header. A form modal opens with the required fields (name, category, status) marked. Add SKU, price, inventory, and description if available.',
        outcome: 'Save emits PRODUCT_UPDATED to ARC after validation. ARC adds it to the catalog and exposes it to templates.',
      },
      {
        title: 'Edit an existing product',
        example: 'Click the pencil icon in the rightmost column. Same form modal as create, prefilled with current values.',
        outcome: 'Changes save back to ARC, with validation gating any save that drops a required field.',
      },
      {
        title: 'Archive instead of delete',
        example: 'Click the X icon in the rightmost column. Sets status to archived; product is no longer offered in templates but the historical reference stays intact.',
        outcome: 'Archived products are hidden from default filter views. Use the Archived pill to find them.',
      },
    ],
    concepts: [
      { term: 'SKU', def: 'Catalog identifier. Optional in Blyn, required by some external systems.' },
      { term: 'Status', def: 'Lifecycle state. Only active products surface in templates by default. Low stock and out of stock are surfaced visually but still available.' },
      { term: 'Sync', def: 'Every save emits PRODUCT_UPDATED. ARC echoes back via the audit log so you can confirm.' },
    ],
    shortcuts: [],
  },
};

// Default fallback help
export const DEFAULT_HELP = {
  title: 'Blyn help',
  intro: 'Need help with this view? Documentation is being added. Check back soon or contact support.',
  tasks: [],
  concepts: [],
  shortcuts: [],
};

// ─────────────────────────────────────────────────────────────────────
// Drawer — open/close via reactive store; rendered by HelpDrawer.tsx
// ─────────────────────────────────────────────────────────────────────

export function openHelpDrawer(viewId) {
  store.set({ helpOpen: true, helpViewId: viewId });
}

export function closeHelpDrawer() {
  store.set({ helpOpen: false });
}

// Stub for legacy views — React pages use <HelpButton> instead
export function helpButton(_viewId) {
  return '';
}
